import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Infinite from "react-infinite";
import { withContentRect } from "react-measure";
import selectn from "selectn";

import Spinner from "../../Library/Spinner";
import StoryCard from "../../Library/StoryCard";
import SummaryCard from "../../Library/SummaryCard";
import EventCard from "../../Library/EventCard";
import StreamAd from "../../Library/StreamAd";

import styles from "./StoriesStream.css";

const contentRectHoc = typeof window === "undefined" ? () => (callback) => callback : withContentRect;
const MeasuredStory = contentRectHoc("client")(({ measureRef, Wrapper, story, storyClassName, showDivider, index, isLastStory, adId, stream, permalinkId }) => (
  <div ref={measureRef} role="article" aria-label={story.headline} tabIndex="-1">
    {showDivider === "start" && <hr className={classNames("mx-4", styles.separator)} aria-hidden={true}/>}
    {
      story.isAd ?
        <StreamAd advert={story} adId={adId} stream={stream}/> :
        <Wrapper story={story} isLastStory={isLastStory} permalinkId={permalinkId} index={index} className={classNames(index !== 0 ? "my-3" : "mb-3", storyClassName)}/>
    }
    {showDivider === "end" && <hr className={classNames("mx-4", styles.separator)} aria-hidden={true}/>}
  </div>
));

class StoriesStream extends Component {
  constructor(props) {
    super(props);

    this.defaultCardHeight = this.props.isSummary || this.props.isEvent ? 200 : 500;

    this._handleInfiniteLoad = this._handleInfiniteLoad.bind(this);

    this.state = {
      loading: false,
      page: 1,
      //prevent auto loading of second page on pages where results aren't rendered yet
      //TODO remove > 1 - this is a hack until permalinks render correctly on server: https://jira.axios.com/browse/SITE-387
      attemptInfiniteLoad: props.stories.results && props.stories.results.length > 1 ? true : undefined,
      elementHeights: {},
      isClient: false,
    };
  }

  componentDidMount() {
    this.setState({ isClient: true });

    if (this.props.stories.isFinished) {
      this.setElementHeights(this.props.stories);
    }
  }

  componentWillReceiveProps(nextProps) {
    const nextStories = nextProps.stories;
    const currentStories = this.props.stories;
    if (nextStories.isFinished && nextStories.results && nextStories.results !== currentStories.results) {
      this.setElementHeights(nextStories);
      this.setState({ attemptInfiniteLoad: true });
    }
    if (!currentStories.isError && nextStories.isError) {
      this.setState({ attemptInfiniteLoad: undefined, loading: false });
    }
    if (!nextStories.results || nextStories.isLoading) {
      this.setState({ page: 1 });
    }
  }

  getStream = (stream) => {
    if (!stream) return "misc";
    if (stream === "top") return "homepage";
    return `section-${stream}`;
  };

  setElementHeights(stories) {
    const results = this.getStoriesAndAds(stories);
    // Because children haven't been measured yet, set height initially to an approximate value: 500px
    let elemHeights = results.reduce((accum, story) => {
      return { ...accum, [story.id]: selectn(`elementHeights.${story.id}`, this.state) || this.defaultCardHeight };
    }, {});
    this.setState({
      // Keep heights that have already been calculated
      elementHeights: elemHeights,
    });
  }

  getStoriesAndAds = (stories) => {
    // Only server render the first 3 stories in any stream and let the rest mount on the client.
    let results = this.state.isClient ? [ ...stories.results ] : stories.results.slice(0, 3);

    if (!this.props.showAds) return results;

    let placedAds = 0;
    let startAd = 2;
    const gap = 4;

    // If there is an adId prop passed in, put an ad first in the response.
    if (this.props.adId) {
      results.splice(0, 0, this.getAd(1, undefined, undefined));
      return results;
    }

    // First ad after 1st post for top
    if (this.props.stream === "top" && stories.results.length >= 2) {
      results.splice(1, 0, this.getAd(1, results[0], results[1]));
      startAd = 7;
    }

    if (results.length < startAd) return results;

    let insertIndex = startAd + placedAds + (placedAds * gap);

    // Ads every 4 posts starting after 6th for top and 2nd for other streams
    while(insertIndex <= results.length) {
      results.splice(insertIndex, 0, this.getAd(placedAds + 2, results[insertIndex - 1], results[insertIndex]));
      placedAds += 1;
      insertIndex = startAd + placedAds + (placedAds * gap);
    }

    return results;
  };

  getAd = (index, beforePost, afterPost) => {
    const postIds = [selectn("id", beforePost), selectn("id", afterPost)];
    const beforeTags = beforePost ? (beforePost.tags || []).map((t) => t.name).concat(selectn("primary_tag.name", beforePost)) : [];
    const afterTags = afterPost ? (afterPost.tags || []).map((t) => t.name).concat(selectn("primary_tag.name", afterPost)) : [];

    return {
      id: `ad_${index}`,
      isAd: true,
      index,
      postIds,
      beforeTags,
      afterTags,
    };
  };

  _updateSize(id, height) {
    if (this.state.elementHeights[id] !== height) {
      this.setState({
        elementHeights: {
          ...this.state.elementHeights,
          [id]: height,
        },
      });
    }
  }

  _handleInfiniteLoad() {
    const page = this.state.page + 1;
    this.setState({ loading: true, page });
    this.props.next(page).then(() => this.setState({ loading: false }));
  }

  render() {
    const { stories, storyClassName, isSummary, isEvent, showUnread } = this.props;
    if (!stories.results || stories.isLoading) return (
      <div className={styles.spinnerWrapper}><Spinner /></div>
    );

    const Wrapper = isSummary ? SummaryCard : isEvent ? EventCard : StoryCard;

    const storyStreamWithAds = this.getStoriesAndAds(stories);
    // We cannot guarantee the uniqueness of IDs in elementHeights so we need
    // to map them back against storyStreamWithAds to return an array of element heights.
    // When ads don't render assign height of 0 or infinite scroll breaks
    const elementHeights = storyStreamWithAds.map((story) => this.state.elementHeights[story.id] || (story.isAd ? 0 : this.defaultCardHeight));
    const containerProps = this.state.isClient ? { useWindowAsScrollContainer: true } : { containerHeight: 2000 };

    return (
      <Infinite
        {...containerProps}
        className={styles.infinite}
        elementHeight={elementHeights}
        infiniteLoadBeginEdgeOffset={this.state.attemptInfiniteLoad && this.defaultCardHeight}
        preloadBatchSize={Infinite.containerHeightScaleFactor(2)}
        preloadAdditionalHeight={Infinite.containerHeightScaleFactor(30)}
        loadingSpinnerDelegate={<Spinner />}
        onInfiniteLoad={this._handleInfiniteLoad}
        isInfiniteLoading={this.state.loading}
      >
        {storyStreamWithAds.map((story, index) => {
          const showDivider = this.props.lastSeenId === story.id && index !== 0 ? "start" :
            !this.props.lastSeenId && index === (stories.results.length - 1) ? "end" : false;
          return <MeasuredStory
            key={story.id}
            index={index}
            adId={this.props.adId}
            stream={this.getStream(this.props.stream)}
            isLastStory={stories.results.length - 1 === index}
            story={story}
            showDivider={showUnread && showDivider}
            Wrapper={Wrapper}
            storyClassName={storyClassName}
            onResize={(contentRect) => {
              this._updateSize(story.id, contentRect.client.height);
            }}
            permalinkId={this.props.permalinkId}
          />;
        })}
      </Infinite>
    );
  }
}

StoriesStream.propTypes = {
  next: PropTypes.func.isRequired,
  stories: PropTypes.object.isRequired,
  isSummary: PropTypes.bool,
  isEvent: PropTypes.bool,
  showUnread: PropTypes.bool,
  storyClassName: PropTypes.string,
};

export default StoriesStream;



// WEBPACK FOOTER //
// ./src/components/Library/StoriesStream/StoriesStream.js